import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import { Label } from "@/components/ui/Label/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import {
  SheetBody,
  SheetClose,
  SheetFooter,
} from "@/components/ui/Sheet/Sheet";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import { OctagonX } from "lucide-react";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { useSignUp } from "../../hooks/signUp/useSignUp";

const schema = z.object({
  choices: z
    .array(z.string("Choices must be strings"))
    .length(3, "3 choices are required")
    .superRefine((choices, ctx) => {
      const seen = new Map<string, number>();
      choices.forEach((v, i) => {
        if (!v) return;
        const prev = seen.get(v);
        if (prev !== undefined) {
          ctx.addIssue({
            code: "custom",
            path: [i],
            message: `Already selected in Preference ${prev + 1}`,
          });
        } else {
          seen.set(v, i);
        }
      });
    }),
});

export type EditMultiWeekSignUpFormFields = z.infer<typeof schema>;

const EditMultiWeekSignUpForm = ({
  data,
  loading,
  onSuccess,
}: {
  data: {
    explorations: any[];
    preferences: any;
    sessionId: string;
  };
  loading: boolean;
  onSuccess: ({
    enrollment,
    preferences,
  }: {
    enrollment?: any;
    preferences?: any;
  }) => void;
}) => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<EditMultiWeekSignUpFormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      choices: data.preferences.choices,
    },
  });

  const choices = watch("choices");

  const { updatePreferences } = useSignUp();

  const onSubmit: SubmitHandler<EditMultiWeekSignUpFormFields> = async (
    formData,
  ) => {
    const result = await updatePreferences(data.sessionId, formData.choices);
    onSuccess({ preferences: result });
  };

  return (
    <>
      <SheetBody>
        <form id="multi-week-sign-up-form" onSubmit={handleSubmit(onSubmit)}>
          <VStack>
            {Array.from({ length: 3 }).map((_, i) => (
              <VStack gap="2px" key={i}>
                <HStack gap="0.25rem">
                  <Label>Preference {i + 1}</Label>
                  {errors.choices?.[i] && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <OctagonX
                          style={{
                            color: "var(--danger)",
                            width: "1rem",
                            height: "1rem",
                            cursor: "default",
                          }}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        {errors.choices?.[i].message}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </HStack>
                {loading ? (
                  <Skeleton style={{ height: "36px", width: "100%" }} />
                ) : (
                  <Controller
                    control={control}
                    name={`choices.${i}`}
                    render={({ field }) => {
                      const taken = new Set(
                        (choices ?? []).filter((v, idx) => v && idx !== i),
                      );

                      return (
                        <Select
                          value={field.value}
                          onValueChange={(v) => field.onChange(v)}
                        >
                          <SelectTrigger aria-invalid={!!errors.choices?.[i]}>
                            <SelectValue placeholder="Select an exploration" />
                          </SelectTrigger>
                          <SelectContent>
                            {data.explorations.map((e) => (
                              <SelectItem
                                key={e._id}
                                value={e._id}
                                disabled={taken.has(e._id)}
                              >
                                {e.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }}
                  />
                )}
              </VStack>
            ))}
          </VStack>
        </form>
      </SheetBody>
      <SheetFooter>
        <Button
          type="submit"
          form="multi-week-sign-up-form"
          style={{ width: "100%" }}
        >
          Save Changes
        </Button>
        <SheetClose asChild>
          <Button variant="outline" style={{ width: "100%" }}>
            Cancel
          </Button>
        </SheetClose>
      </SheetFooter>
    </>
  );
};

export { EditMultiWeekSignUpForm };
